import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PatientMain.css";
import axios from "axios";
import logo from '../assets/CAROLINA.png';
import DatePicker from 'rsuite/DatePicker';
import 'rsuite/DatePicker/styles/index.css';
import { BsCloudUpload, BsTrash, BsCheckCircle, BsXCircle, BsInfoCircle, BsFolder, BsChevronDown, BsChevronRight, BsHourglass } from "react-icons/bs";
import { FaCheck } from "react-icons/fa6";
import { makeZip } from 'client-zip';
import PRACTICE_CONFIGS from '../config/practiceConfig';
import { extractDicomMetadata } from '../utils/dicomParser';
import { API_URLS } from '../config/api';

const API_URL = API_URLS.UPLOAD;
const SUBMISSION_URL = API_URLS.SUBMISSION;
const MAX_PATIENTS = 10;

const BLOCKED_EXTENSIONS = new Set([
    '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
    '.js', '.jsx', '.vbs', '.wsf', '.ps1', '.sh', '.bash',
    '.php', '.py', '.rb', '.pl', '.jar', '.dll', '.sys',
    '.html', '.htm', '.svg',
]);

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

const getFileExtension = (name) => {
    const dot = name.lastIndexOf('.');
    return dot !== -1 ? name.slice(dot).toLowerCase() : '';
};

const validateFile = (file) => {
    const ext = getFileExtension(file.name);
    if (BLOCKED_EXTENSIONS.has(ext)) return `File type "${ext}" is not allowed: ${file.name}`;
    if (file.size > MAX_FILE_SIZE) return `File exceeds 5GB limit: ${file.name}`;
    return null;
};

const sanitizePathName = (name) => {
    return name
        .replace(/\.\./g, '')
        .replace(/[/\\%<>:"|?*\x00-\x1F]/g, '_')
        .replace(/^\.+/, '')
        .replace(/_+/g, '_')
        .trim();
};

const sanitizeInput = (value) => {
    if (!value) return '';
    return value
        .replace(/<[^>]*>/g, '')
        .replace(/[<>"'`;()]/g, '')
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
        .trim();
};

function generateRandomString(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
        result += characters.charAt(randomValues[i] % characters.length);
    }
    return result;
}

const STUDY_OPTIONS = [
    { label: 'Report', value: 'Report' },
    { label: 'SimPlant Study', value: 'SimPlant Study' },
];
const STUDY_PLUS_OPTIONS = [
    { label: 'No Add-ons', value: 'No Add-ons' },
    { label: '+$25 Endo/Implant Evaluation 3 or more teeth', value: '+$25 Endo/Implant Evaluation 3 or more teeth' },
    { label: '+$25 Comparison Report', value: '+$25 Comparison Report' },
    { label: '+$25 Rush Case (less than 24 hours)', value: '+$25 Rush Case (less than 24 hours)' },
];
const PAYMENT_OPTIONS = [
    { label: 'Credit Card (to be collected at a later time if not already on file)', value: 'Credit Card' },
    { label: 'Payment Link (will be sent via email along with the report)', value: 'Payment Link' },
    { label: 'Invoice (will be sent via email along with the report)', value: 'Invoice' },
];

let patientCounter = 0;

function formatPatientName(folderName) {
    let name = folderName.replace(/[_-]/g, ' ').trim();
    if (!name.includes(' ')) {
        patientCounter++;
        name = name + ' ' + patientCounter;
    }
    return name;
}

async function createPatientFolder(name, files) {
    const folder = {
        id: generateRandomString(8),
        folderName: name,
        patientName: formatPatientName(name),
        files: files,
        status: 'pending',
        progress: 0,
        error: null,
        scanDate: null,
        dob: null,
        report: 'Report',
        reportPlus: 'No Add-ons',
        paymentMethod: 'Credit Card',
        regionsOfInterest: '',
    };

    try {
        const metadata = await extractDicomMetadata(files);
        if (metadata) {
            if (metadata.patientName) folder.patientName = metadata.patientName;
            if (!folder.scanDate && metadata.studyDate) folder.scanDate = metadata.studyDate;
            if (!folder.dob && metadata.patientDob) folder.dob = metadata.patientDob;
            if (metadata.studyDescription) folder.regionsOfInterest = metadata.studyDescription;
        }
    } catch (_) {
        // DICOM metadata extraction is best-effort; folder name is used as fallback
    }

    return folder;
}

function PracticeReferralForm() {
    const navigate = useNavigate();
    const { practiceSlug } = useParams();
    const practiceConfig = PRACTICE_CONFIGS[practiceSlug];

    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const showToast = useCallback((message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
    }, []);

    const [ip, setIP] = useState('');
    const [patientFolders, setPatientFolders] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [expandedPatient, setExpandedPatient] = useState(null);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        axios.post("https://ipinfo.io", null, { timeout: 5000 })
            .then((response) => setIP(response.data.ip))
            .catch(() => { setIP('unavailable'); });
    }, []);

    const handleFolderInput = async (event) => {
        try {
            const files = [...event.target.files];
            if (!files.length) return;

            for (const f of files) {
                const err = validateFile(f);
                if (err) {
                    showToast(err, "error");
                    return;
                }
            }

            const folderMap = {};
            files.forEach(file => {
                const rootFolder = file.webkitRelativePath.split('/')[0];
                if (!folderMap[rootFolder]) folderMap[rootFolder] = [];
                folderMap[rootFolder].push(file);
            });

            if (Object.keys(folderMap).length === 0) {
                showToast("No valid folders detected. Each folder should be named Firstname Lastname.", "error");
                return;
            }

            const newFolders = await Promise.all(
                Object.entries(folderMap).map(([name, folderFiles]) =>
                    createPatientFolder(name, folderFiles)
                )
            );

            addFoldersToState(newFolders);
        } catch (err) {
            showToast("Error reading folders. Please try again.", "error");
        }

        event.target.value = null;
    };

    const readEntryFiles = (entry, rootName) => {
        return new Promise((resolve) => {
            if (entry.isFile) {
                entry.file(file => {
                    Object.defineProperty(file, 'webkitRelativePath', {
                        value: rootName + '/' + entry.fullPath.split('/').slice(1).join('/'),
                        writable: false,
                    });
                    resolve([file]);
                }, () => resolve([]));
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                const allEntries = [];
                const readBatch = () => {
                    reader.readEntries(async (entries) => {
                        if (entries.length === 0) {
                            const files = await Promise.all(allEntries.map(e => readEntryFiles(e, rootName)));
                            resolve(files.flat());
                        } else {
                            allEntries.push(...entries);
                            readBatch();
                        }
                    }, () => resolve([]));
                };
                readBatch();
            } else {
                resolve([]);
            }
        });
    };

    const addFoldersToState = (newFolders) => {
        setPatientFolders(prev => {
            const combined = [...prev, ...newFolders];
            if (combined.length > MAX_PATIENTS) {
                showToast(`Maximum ${MAX_PATIENTS} patients per batch. ${combined.length - MAX_PATIENTS} folder(s) were not added.`, "error");
                return combined.slice(0, MAX_PATIENTS);
            }
            if (prev.length === 0 && newFolders.length > 0) {
                setExpandedPatient(newFolders[0].id);
            }
            return combined;
        });
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        try {
            const items = e.dataTransfer.items;
            if (!items || items.length === 0) return;

            const folderPromises = [];
            for (let i = 0; i < items.length; i++) {
                const entry = items[i].webkitGetAsEntry?.() || items[i].getAsEntry?.();
                if (entry && entry.isDirectory) {
                    folderPromises.push(
                        readEntryFiles(entry, entry.name).then(files => ({ name: entry.name, files }))
                    );
                }
            }

            if (folderPromises.length === 0) {
                showToast("Please drop folders, not individual files. Each folder should be named Firstname Lastname.", "error");
                return;
            }

            const folders = await Promise.all(folderPromises);

            for (const folder of folders) {
                if (folder.files.length === 0) {
                    showToast(`Folder "${folder.name}" is empty and was skipped.`, "error");
                    continue;
                }
                for (const f of folder.files) {
                    const err = validateFile(f);
                    if (err) {
                        showToast(err, "error");
                        return;
                    }
                }
            }

            const validFolders = folders.filter(f => f.files.length > 0);
            if (validFolders.length === 0) {
                showToast("No valid folders found. Please ensure folders contain files.", "error");
                return;
            }

            const newFolders = await Promise.all(
                validFolders.map(f => createPatientFolder(f.name, f.files))
            );
            addFoldersToState(newFolders);
        } catch (err) {
            showToast("Error reading dropped folders. Please try again.", "error");
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const updatePatientField = (id, field, value) => {
        setPatientFolders(prev => prev.map(p =>
            p.id === id ? { ...p, [field]: value } : p
        ));
    };

    const removePatient = (id) => {
        try {
            setPatientFolders(prev => prev.filter(p => p.id !== id));
            if (expandedPatient === id) setExpandedPatient(null);
        } catch (err) {
            showToast("Error removing patient folder.", "error");
        }
    };

    const removeAllPatients = () => {
        if (!window.confirm(`Remove all ${patientFolders.length} patient folders?`)) return;
        setPatientFolders([]);
        setExpandedPatient(null);
    };

    const toggleExpand = (id) => {
        setExpandedPatient(prev => prev === id ? null : id);
    };

    const updatePatientStatus = (id, status, progress, error = null, progressLabel = null) => {
        setPatientFolders(prev => prev.map(p =>
            p.id === id ? { ...p, status, progress, error, progressLabel } : p
        ));
    };

    const waitForOnline = () => {
        return new Promise((resolve) => {
            if (navigator.onLine) { resolve(); return; }
            const handler = () => { window.removeEventListener('online', handler); resolve(); };
            window.addEventListener('online', handler);
        });
    };

    const uploadPatient = async (patient, practitioner_name_clean) => {
        const fileId = generateRandomString(12);
        const patientNameSafe = sanitizePathName(patient.patientName);
        const fileName = patientNameSafe + '_' + sanitizePathName(practitioner_name_clean) + '.zip';

        updatePatientStatus(patient.id, 'uploading', 0);
        await streamZipToParts(fileId, fileName, patient);

        updatePatientStatus(patient.id, 'submitting', 100);
        const formData = {
            id: fileId,
            practice_name: practiceConfig.practice_name,
            practitioner_name: sanitizePathName(practitioner_name_clean),
            contact_email: practiceConfig.contact_email,
            office_number: null,
            patient_name: patientNameSafe,
            patient_dob: patient.dob ? new Date(patient.dob).toISOString().slice(0, 10) : null,
            patient_gender: null,
            patient_ethnicity: null,
            patient_scan_date: patient.scanDate ? new Date(patient.scanDate).toISOString().slice(0, 10).replace('/', '_') : null,
            report: sanitizeInput(patient.report),
            report_plus: sanitizeInput(patient.reportPlus),
            payment_method: sanitizeInput(patient.paymentMethod),
            regions_of_interest: sanitizeInput(patient.regionsOfInterest),
            file_name: fileName,
            file_type: 'application/zip',
            dttm: new Date(),
            user_ip: ip,
        };

        if (!formData.patient_dob) {
            throw new Error('Patient Date of Birth is required.');
        }
        if (!formData.patient_scan_date) {
            throw new Error('Patient Scan Date is required.');
        }

        const submitResponse = await axios.post(SUBMISSION_URL, {
            headers: { "Content-Type": "multipart/form-data" },
            body: JSON.stringify(formData),
        });

        if (submitResponse.data?.statusCode && submitResponse.data.statusCode !== 200) {
            throw new Error(submitResponse.data?.errorMessage || 'Referral metadata submission failed.');
        }
        if (submitResponse.data?.errorMessage) {
            throw new Error(submitResponse.data.errorMessage);
        }
    };

    // Zip a patient's folder and stream it straight into S3 multipart parts.
    // client-zip reads each File lazily (never loads a whole file into memory) and
    // the archive is consumed in chunks, so peak memory is ~one 16MB part instead
    // of the whole 3-4GB zip — which was OOM-crashing low-RAM tabs.
    const streamZipToParts = async (fileId, fileName, patient) => {
        const PART_SIZE = 16 * 1024 * 1024;
        const MAX_RETRIES = 3;
        const estimatedTotal = patient.files.reduce((a, f) => a + (f.size || 0), 0) || 1;

        const initResponse = await axios.post(API_URL, {
            headers: { "Content-Type": "multipart/form-data" },
            body: { fileID: fileId, fileName: fileName, fileOperation: "createUploadID" }
        }, { timeout: 30000 });
        const initData = initResponse.data.body ?? initResponse.data;
        let uploadId;
        try { uploadId = typeof initData === 'object' ? initData.UploadId : JSON.parse(initData).UploadId; }
        catch (_) { throw new Error('Failed to parse multipart upload response'); }
        if (!uploadId) throw new Error('Failed to initialize multipart upload');

        let uploadedBytes = 0;

        const uploadPart = async (num, blob, attempt = 1) => {
            try {
                if (!navigator.onLine) {
                    updatePatientStatus(patient.id, 'uploading', null, null, 'Connection lost — waiting...');
                    await waitForOnline();
                }
                const response = await axios.post(API_URL, {
                    headers: { "Content-Type": "multipart/form-data" },
                    body: { fileID: fileId, fileName: fileName, fileOperation: "getSignedUrl", uploadId, partNumber: num }
                }, { timeout: 30000 });
                let signedUrl = response.data.body ?? response.data;
                if (typeof signedUrl === 'string' && signedUrl.startsWith('"')) { try { signedUrl = JSON.parse(signedUrl); } catch (_) {} }
                if (!signedUrl || typeof signedUrl !== 'string') throw new Error(`Failed to get signed URL for part ${num}`);

                const base = uploadedBytes;
                let last = 0;
                await axios.put(signedUrl, blob, {
                    headers: { 'Content-Type': 'application/octet-stream' },
                    transformRequest: [(d) => d],
                    onUploadProgress: (e) => {
                        const now = Date.now();
                        if (now - last < 200) return;
                        last = now;
                        updatePatientStatus(patient.id, 'uploading', Math.min(100, Math.round(((base + e.loaded) * 100) / estimatedTotal)));
                    }
                });
            } catch (error) {
                if (attempt < MAX_RETRIES) {
                    await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 8000)));
                    return uploadPart(num, blob, attempt + 1);
                }
                throw new Error(`Upload failed for part ${num} after ${MAX_RETRIES} attempts`);
            }
        };

        // client-zip streams from each File lazily (never loads a whole file), and
        // pulling one chunk at a time applies backpressure so peak memory is ~one part.
        const inputs = patient.files.map((f) => ({
            input: f,
            name: f.webkitRelativePath || f.path || f.name,
            lastModified: f.lastModified ? new Date(f.lastModified) : new Date(),
            size: f.size,
        }));
        const reader = makeZip(inputs).getReader();

        let buffered = [];
        let bufferedLen = 0;
        let partNumber = 0;

        const flushPart = async () => {
            const partBlob = new Blob(buffered);
            buffered = [];
            bufferedLen = 0;
            partNumber += 1;
            updatePatientStatus(patient.id, 'uploading', null, null, `${partNumber} part${partNumber === 1 ? '' : 's'}`);
            await uploadPart(partNumber, partBlob);
            uploadedBytes += partBlob.size;
        };

        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffered.push(value);
                bufferedLen += value.length;
                if (bufferedLen >= PART_SIZE) {
                    await flushPart();
                }
            }
            if (bufferedLen > 0 || partNumber === 0) await flushPart();
        } finally {
            try { reader.releaseLock(); } catch (_) {}
        }

        updatePatientStatus(patient.id, 'uploading', 100, null, 'Finalizing...');
        const completeResponse = await axios.post(API_URL, {
            headers: { "Content-Type": "multipart/form-data" },
            body: { fileID: fileId, fileName: fileName, uploadId, fileOperation: "completeMultipartUpload" }
        }, { timeout: 60000 });
        const completeData = completeResponse.data.body ?? completeResponse.data;
        let result;
        try { result = typeof completeData === 'string' ? JSON.parse(completeData) : completeData; }
        catch (_) { throw new Error('Failed to parse upload completion response'); }
        if (completeResponse.status !== 200 || result?.error) {
            throw new Error('Failed to finalize upload: ' + (result?.error || completeResponse.status));
        }
    };

    const handleBatchSubmit = async (event) => {
        event.preventDefault();
        if (patientFolders.length === 0) {
            showToast("Please add at least one patient folder before submitting.", "error");
            return;
        }

        for (const p of patientFolders) {
            if (p.files.length === 0) {
                showToast(`Folder "${p.patientName}" has no files.`, "error");
                return;
            }
        }

        setSubmitting(true);
        setExpandedPatient(null);

        try {
            const practitioner_name_clean = practiceConfig.practitioner_name.replace(/Dr\. |dr\. |Dr |dr /g, '');

            for (const patient of patientFolders) {
                try {
                    await uploadPatient(patient, practitioner_name_clean);
                    updatePatientStatus(patient.id, 'submitted', 100);
                } catch (error) {
                    updatePatientStatus(patient.id, 'error', 0, error.message || 'Upload failed');
                }
            }
        } catch (error) {
            showToast("An unexpected error occurred during submission. Please try again.", "error");
        }

        setSubmitting(false);
        setSubmitted(true);
    };

    const successCount = patientFolders.filter(p => p.status === 'submitted').length;
    const errorCount = patientFolders.filter(p => p.status === 'error').length;

    if (!practiceConfig) {
        return (
            <div id="body">
                <div className="page">
                    <div className="navbar" style={{ justifyContent: 'flex-start', paddingLeft: '48px' }}>
                        <img alt="Carolina OMF Imaging" style={{ height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0' }} src={logo} />
                    </div>
                    <div className="form-page-content" style={{ textAlign: 'center', paddingTop: '80px' }}>
                        <h2 style={{ color: '#1A2A45' }}>Practice Not Found</h2>
                        <p style={{ color: '#555', marginTop: '12px' }}>The practice URL "{practiceSlug}" is not recognized.</p>
                        <button className="btn" style={{ marginTop: '24px', background: '#1F4F59', color: '#fff', padding: '10px 24px', borderRadius: '6px' }}
                            onClick={() => navigate('/patient-referral')}>
                            Go to Patient Referral Form
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div id="body">
                <div className="page">
                    <div className="navbar" style={{ justifyContent: 'flex-start', paddingLeft: '48px' }}>
                        <img alt="Carolina OMF Imaging" style={{ height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0' }} src={logo} />
                    </div>
                    <div className="form-page-content">
                        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 20px" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: "500px", width: "100%" }}>
                                <div style={{ backgroundColor: errorCount > 0 ? "#f8d7da" : "#A4D4E6", borderRadius: "50%", padding: "20px", marginBottom: "10px" }}>
                                    {errorCount > 0
                                        ? <BsInfoCircle size={40} color="#1A2A45" />
                                        : <FaCheck size={40} color="#1A2A45" />
                                    }
                                </div>
                                <h2 style={{ color: "#1F4F59", fontSize: "24px", fontWeight: "600", margin: "0 0 8px 0" }}>
                                    {errorCount > 0 ? 'Batch Partially Submitted' : 'Referrals Submitted'}
                                </h2>
                                <p style={{ color: "#666", fontSize: "14px", margin: "0 0 24px 0" }}>
                                    {successCount} of {patientFolders.length} referrals submitted successfully.
                                    {errorCount > 0 && ` ${errorCount} failed.`}
                                </p>

                                <div style={{ width: "100%", backgroundColor: "#f8f9fa", borderRadius: "10px", padding: "16px 20px", marginBottom: "16px" }}>
                                    {patientFolders.map(p => (
                                        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", borderBottom: "1px solid #eee" }}>
                                            {p.status === 'submitted'
                                                ? <BsCheckCircle color="#2ecc71" size={16} />
                                                : <BsXCircle color="#e74c3c" size={16} />
                                            }
                                            <span style={{ flex: 1, color: "#1A2A45", fontSize: "14px", fontWeight: "500" }}>{p.patientName}</span>
                                            {p.error && <span style={{ color: "#e74c3c", fontSize: "12px" }}>{p.error}</span>}
                                        </div>
                                    ))}
                                </div>

                                <div style={{ width: "100%", backgroundColor: "#f8f9fa", borderRadius: "10px", padding: "16px 20px", marginBottom: "16px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: "#666", fontSize: "14px" }}>Contact Us</span>
                                        <a href="mailto:ncolvin@carolinaomfimaging.com" style={{ color: "#1F4F59", fontSize: "14px", fontWeight: "500", textDecoration: "none" }}>ncolvin@carolinaomfimaging.com</a>
                                    </div>
                                </div>

                                <div style={{ backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "14px 20px", width: "100%", textAlign: "center", marginBottom: "24px" }}>
                                    <span style={{ color: "#1A2A45", fontSize: "14px", fontWeight: "500" }}>Expect your reports in 3-5 Business Days</span>
                                </div>

                                <button className="btn" style={{ background: '#1F4F59', color: '#fff', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '500' }}
                                    onClick={() => { setSubmitted(false); setPatientFolders([]); patientCounter = 0; }}>
                                    Submit Another Batch
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="body">
            {toast.show && (
                <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
                    <div className="toast-content">
                        {toast.type === 'success' && <BsCheckCircle className="toast-icon" />}
                        {toast.type === 'error' && <BsXCircle className="toast-icon" />}
                        {toast.type === 'info' && <BsInfoCircle className="toast-icon" />}
                        <span>{toast.message}</span>
                    </div>
                </div>
            )}
            <div className="page">
                <div className="navbar" style={{ justifyContent: 'flex-start', paddingLeft: '48px' }}>
                    <img alt="Carolina OMF Imaging" style={{ height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0' }} src={logo} />
                </div>
                <div className="form-page-content">
                    <label id="patientReferralTitle" className="form-label">Batch Patient Referral</label>

                    <div className="practice-banner">
                        <div className="practice-banner-field">
                            <span className="practice-banner-label">Practice</span>
                            <span className="practice-banner-value">{practiceConfig.practice_name}</span>
                        </div>
                        <div className="practice-banner-field">
                            <span className="practice-banner-label">Practitioner</span>
                            <span className="practice-banner-value">{practiceConfig.practitioner_name}</span>
                        </div>
                        <div className="practice-banner-field">
                            <span className="practice-banner-label">Contact Email</span>
                            <span className="practice-banner-value">{practiceConfig.contact_email}</span>
                        </div>
                    </div>

                    <form onSubmit={handleBatchSubmit}>
                        <div className="batch-upload-layout">
                            <div className="batch-upload-patients">
                                {patientFolders.length > 0 ? (
                                    <>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#1A2A45' }}>
                                                {patientFolders.length} patient{patientFolders.length !== 1 ? 's' : ''} detected
                                            </span>
                                            {!submitting && (
                                                <span id="fileDisplayRemove" onClick={removeAllPatients}>
                                                    <BsTrash size={16} /> Remove all
                                                </span>
                                            )}
                                        </div>

                                        <div className="patient-card-list">
                                            {patientFolders.map(p => (
                                                <div key={p.id} className={`patient-card patient-status-${p.status}`}>
                                                    <div className="patient-card-header" onClick={() => p.status === 'pending' && toggleExpand(p.id)}>
                                                        <span className="batch-status-icon">
                                                            {p.status === 'pending' && <BsFolder color="#1F4F59" />}
                                                            {p.status === 'zipping' && <BsHourglass color="#f39c12" />}
                                                            {p.status === 'uploading' && <BsCloudUpload color="#3498db" />}
                                                            {p.status === 'submitting' && <BsCloudUpload color="#3498db" />}
                                                            {p.status === 'submitted' && <BsCheckCircle color="#2ecc71" />}
                                                            {p.status === 'error' && <BsXCircle color="#e74c3c" />}
                                                        </span>
                                                        <span className="patient-card-name">{p.patientName}</span>
                                                        <span className="patient-list-files">{p.files.length} file{p.files.length !== 1 ? 's' : ''}</span>
                                                        {(p.status === 'uploading' || p.status === 'zipping') && (
                                                            <span className="patient-list-progress">
                                                                {p.progress}%{p.progressLabel ? ` (${p.progressLabel})` : ''}
                                                            </span>
                                                        )}
                                                        {p.status === 'pending' && (
                                                            <>
                                                                <span className="patient-card-chevron">
                                                                    {expandedPatient === p.id ? <BsChevronDown size={14} /> : <BsChevronRight size={14} />}
                                                                </span>
                                                                <button type="button" className="patient-list-remove" onClick={(e) => { e.stopPropagation(); removePatient(p.id); }}>
                                                                    <BsTrash size={13} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {p.error && <span className="patient-list-error">{p.error}</span>}
                                                    </div>

                                                    {expandedPatient === p.id && p.status === 'pending' && (
                                                        <div className="patient-card-fields">
                                                            <div className="patient-card-grid">
                                                                <div>
                                                                    <label id="formSubLabel">Patient Name</label>
                                                                    <input id="dropdown-input" type="text" value={p.patientName} onChange={(e) => updatePatientField(p.id, 'patientName', e.target.value)} placeholder="Patient Name" />
                                                                </div>
                                                                <div>
                                                                    <label id="formSubLabel">Patient Date of Birth</label>
                                                                    <DatePicker oneTap format="MM/dd/yyyy" value={p.dob} onChange={(date) => updatePatientField(p.id, 'dob', date)} placeholder="mm/dd/yyyy" block />
                                                                </div>
                                                                <div>
                                                                    <label id="formSubLabel">Patient Scan Date</label>
                                                                    <DatePicker oneTap format="MM/dd/yyyy" value={p.scanDate} onChange={(date) => updatePatientField(p.id, 'scanDate', date)} placeholder="mm/dd/yyyy" block />
                                                                </div>
                                                                <div>
                                                                    <label id="formSubLabel">Report</label>
                                                                    <select id="dropdown-input" value={p.report} onChange={(e) => updatePatientField(p.id, 'report', e.target.value)}>
                                                                        {STUDY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label id="formSubLabel">Report Plus</label>
                                                                    <select id="dropdown-input" value={p.reportPlus} onChange={(e) => updatePatientField(p.id, 'reportPlus', e.target.value)}>
                                                                        {STUDY_PLUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label id="formSubLabel">Preferred Payment Method</label>
                                                                    <select id="dropdown-input" value={p.paymentMethod} onChange={(e) => updatePatientField(p.id, 'paymentMethod', e.target.value)}>
                                                                        {PAYMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <label id="formSubLabel">Regions of Interest</label>
                                                            <textarea id="regions-input" placeholder="Regions of Interest / Clinical Signs / Symptoms" value={p.regionsOfInterest} onChange={(e) => updatePatientField(p.id, 'regionsOfInterest', e.target.value)} />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <label style={{ marginTop: '10px', display: 'block', fontSize: '12px' }}>*Reports will take 3-5 business days to complete</label>
                                        <button className="btn" id="submitFile" type="submit" disabled={patientFolders.length === 0 || submitting} style={{ marginTop: '12px' }}>
                                            {submitting ? 'Submitting...' : `Submit ${patientFolders.length} Referral${patientFolders.length !== 1 ? 's' : ''}`}
                                        </button>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '13px', padding: '40px 20px', textAlign: 'center' }}>
                                        Patient folders will appear here after uploading.
                                    </div>
                                )}
                            </div>

                            <div className="batch-upload-drop">
                                <label id="formSubLabel">Upload patient folders (max {MAX_PATIENTS}) *</label>
                                <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                    Each folder should be named <strong>Firstname Lastname</strong>
                                </p>

                                <div className={`fileDrop${dragging ? ' fileDrop-active' : ''}`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    style={{ flex: 1, minHeight: '280px' }}
                                >
                                    <div style={{ display: 'block', textAlign: 'center' }}>
                                        <BsCloudUpload size={35} color="#1F4F59" className="fileDrop-icon" style={{ margin: '5px' }} />
                                        <label style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2px' }}>
                                            {dragging ? 'Drop folders here' : 'Drag & drop folders or browse'}
                                        </label>
                                        <div style={{ display: "flex", justifyContent: 'space-evenly', alignItems: "center" }}>
                                            <div>
                                                <input id="batch_folder_picker" className="form-control" style={{ display: "none" }} name="file" multiple directory="" webkitdirectory="" type="file" onChange={handleFolderInput} />
                                                <label className="btn" id="pickerFile" htmlFor="batch_folder_picker">Browse Folders</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default PracticeReferralForm;
